// DemandEditEvent actions and their changelog layers. Client-shared because the changelog filters
// by layer on the server; both sides must map alike.

import type { DemandBaselineSource } from "@generated/types"

import type { NonTradingSummary, PublishSkipCounts } from "~/utils/demandPublishSkips"

/** What an Introduced NOOS variant's baseline is read from (`DemandBaselineSource.source`). */
export type BaselineSource = DemandBaselineSource["source"]
export const BASELINE_SOURCES = [
  "ownSales",
  "matches",
  "normalized",
] as const satisfies readonly BaselineSource[]

// Null on older ledger rows, which read as "human".
export type DemandActorType = "human" | "agent"

export type DemandEditAction =
  | "override_set"
  | "override_clear"
  | "dated_override_set"
  // Where?'s split: each cell's share of the variant total (`SHARE_SENTINEL` rows).
  | "share_set"
  | "adjustment_add"
  | "adjustment_edit"
  | "adjustment_delete"
  | "adjustment_reorder"
  | "insight_set"
  | "size_split_set"
  | "size_split_clear"
  | "publish"
  | "match_approve"
  | "match_remove"
  | "match_basis_set"
  | "match_none_set"
  | "match_none_clear"
  // NOOS Introduced: plan on own sales or on matches, overriding the engine's rule (`DemandBaselineSource`).
  | "baseline_source_set"
  // NOOS: the annual level the NPI plans at (`ForecastNpiAggregated.usersetValue`).
  | "npi_level_set"
  // NOOS: how much of the NPI's demand follows intro vs calendar seasonality
  // (`ForecastNpiAggregated.usersetIntroWeight`).
  | "npi_intro_weight_set"
  // Assortment tab Framing answer (`DemandFramingAnswer`).
  | "framing_set"
  // A collection-wide insight answered on one hierarchy node (`DemandSetupInsightNode`).
  | "node_insight_set"
  // Company-wide planning scope (`DemandExcludedCombination`), written to every workspace.
  | "combinations_excluded_set"
  // "No adjustments needed" (`DemandWorkspace.insightsReviewedAt`).
  | "insights_reviewed_set"
  // Reviewed/Pending per variant before publishing (`DemandVariantReview`).
  | "variant_review_set"

// The first four mirror the composition stack; "plan" (publish) moves no layer.
export const DEMAND_CHANGE_LAYERS = ["manual", "on-top", "setup", "size", "plan"] as const

export type DemandChangeLayer = (typeof DEMAND_CHANGE_LAYERS)[number]

// Adjustments are "on-top" here; layerForAction reclassifies them by stage.
export const LAYER_BY_ACTION: Record<DemandEditAction, DemandChangeLayer> = {
  override_set: "manual",
  override_clear: "manual",
  dated_override_set: "manual",
  share_set: "manual",
  size_split_set: "size",
  size_split_clear: "size",
  insight_set: "setup",
  match_approve: "setup",
  match_remove: "setup",
  match_basis_set: "setup",
  match_none_set: "setup",
  match_none_clear: "setup",
  baseline_source_set: "setup",
  npi_level_set: "setup",
  npi_intro_weight_set: "setup",
  framing_set: "setup",
  node_insight_set: "setup",
  combinations_excluded_set: "setup",
  insights_reviewed_set: "setup",
  variant_review_set: "setup",
  adjustment_add: "on-top",
  adjustment_edit: "on-top",
  adjustment_delete: "on-top",
  adjustment_reorder: "on-top",
  publish: "plan",
}

// Whether the action changed the authored plan and bumps `DemandWorkspace.version`; `true` is the
// safe answer. publish is false: bumping past the version it just recorded reads as "edited since
// publish". So are insights_reviewed_set and variant_review_set, which move no number.
export const CHANGES_AUTHORED_STATE: Record<DemandEditAction, boolean> = {
  override_set: true,
  override_clear: true,
  dated_override_set: true,
  share_set: true,
  size_split_set: true,
  size_split_clear: true,
  insight_set: true,
  match_approve: true,
  match_remove: true,
  match_basis_set: true,
  match_none_set: true,
  match_none_clear: true,
  baseline_source_set: true,
  npi_level_set: true,
  npi_intro_weight_set: true,
  adjustment_add: true,
  adjustment_edit: true,
  adjustment_delete: true,
  adjustment_reorder: true,
  framing_set: true,
  node_insight_set: true,
  combinations_excluded_set: true,
  insights_reviewed_set: false,
  variant_review_set: false,
  publish: false,
}

// For `getChangelogIntegrity`'s ledger-vs-version cross-check.
export const AUTHORED_ACTIONS = (Object.keys(CHANGES_AUTHORED_STATE) as DemandEditAction[]).filter(
  (a) => CHANGES_AUTHORED_STATE[a],
)

/**
 * Whether an action can move one cube cell's demand number (`listDemandCellHistory`). False:
 * `publish` (a crossing, not an edit); `size_split_*` (splits a cell's total, never moves it);
 * `combinations_excluded_set` (a draft display rule, though publish does fold demand off non-traded
 * combinations).
 */
const MOVES_CELL_DEMAND: Record<DemandEditAction, boolean> = {
  override_set: true,
  override_clear: true,
  dated_override_set: true,
  share_set: true,
  insight_set: true,
  match_approve: true,
  match_remove: true,
  match_basis_set: true,
  match_none_set: true,
  match_none_clear: true,
  baseline_source_set: true,
  npi_level_set: true,
  npi_intro_weight_set: true,
  adjustment_add: true,
  adjustment_edit: true,
  adjustment_delete: true,
  // Order decides how percentages compound.
  adjustment_reorder: true,
  framing_set: true,
  node_insight_set: true,
  size_split_set: false,
  size_split_clear: false,
  combinations_excluded_set: false,
  insights_reviewed_set: false,
  variant_review_set: false,
  publish: false,
}

export const CELL_HISTORY_ACTIONS = (Object.keys(MOVES_CELL_DEMAND) as DemandEditAction[]).filter(
  (a) => MOVES_CELL_DEMAND[a],
)

// Whether the changelog offers a revert; `false` is the safe answer. A revert restores rows from
// the `_history` temporal twin, so the layer needs a twin and the event must name the rows.
// Module-private: read through `isRevertibleEvent`.
const REVERTIBLE_ACTIONS = {
  override_set: true,
  override_clear: true,
  dated_override_set: true,
  share_set: true,
  size_split_set: true,
  size_split_clear: true,
  insight_set: true,
  adjustment_add: true,
  adjustment_edit: true,
  adjustment_delete: true,
  adjustment_reorder: true,
  publish: false,
  match_approve: false,
  match_remove: false,
  match_basis_set: false,
  match_none_set: false,
  match_none_clear: false,
  baseline_source_set: false,
  npi_level_set: false,
  npi_intro_weight_set: false,
  framing_set: false,
  node_insight_set: false,
  combinations_excluded_set: false,
  insights_reviewed_set: false,
  variant_review_set: false,
} as const satisfies Record<DemandEditAction, boolean>

// The `true` half as a type, so the changelog's key-space registry can be exhaustive over it.
export type RevertibleAction = {
  [K in DemandEditAction]: (typeof REVERTIBLE_ACTIONS)[K] extends true ? K : never
}[DemandEditAction]

// Revertibility of one event; shared by the changelog UI and the server. A stored action since
// dropped from the union has no entry, hence `=== true`.
export function isRevertibleEvent(action: DemandEditAction): action is RevertibleAction {
  return REVERTIBLE_ACTIONS[action] === true
}

// One payload shape per action: a writer contract. The ledger is append-only, so a field is
// required only when every writer writes it and no reader chains through it. Unknown keys are
// caught at direct `emitDemandEditEvent` sites, not through `authorEdit`'s `event` callback.
// JSON-only, so a `Date` is a compile error (Kysely would store a full ISO datetime).
// `stage` must stay top-level (`meta ->> 'stage'` in publish.ts and changelog.ts); arrays must stay
// flat and top-level (`trimEventMeta` bounds only those).
type JsonValue =
  | string
  | number
  | boolean
  | null
  | JsonValue[]
  | { [key: string]: JsonValue | undefined }

// Identity; makes the map below require a payload per action and reject `Date` values.
type MetaMap<T extends Record<DemandEditAction, { [key: string]: JsonValue | undefined } | null>> =
  T

type AdjustmentAudit = {
  name: string
  type: string
  stage: string
  target: string
  theme: string | null
  windowed: boolean
  startDate: string | null
  endDate: string | null
}

type MatchScope = { kind: "fashion" | "noos"; matchType: "baseline" | "forecast" }

type PublishMeta = {
  publishId: number
  note: string | null
  variantCount: number
  skippedVariantIds: string[]
  // Optional fields here are absent on older publishes.
  skippedByReason?: PublishSkipCounts
  notOwnedVariantIds: string[]
  nonTrading?: NonTradingSummary
  sizeSplitVariantCount?: number
  archivedAdjustmentCount?: number
  rebuiltVariantCount: number
  // Mirrors RebuildOutcome (publishRebuildOutcome.ts); client-shared, so it can't import it.
  rebuild: { status: "ok" } | { status: "skipped_no_plv8" } | { status: "failed"; message: string }
}

// Key names are declared as stored (`ids`, `variants`, singular `adjustmentId`): renaming would
// orphan existing rows.
type DemandEditMetaByAction = MetaMap<{
  override_set: null
  override_clear: null
  size_split_clear: null
  // The span, not the weeks (see the emit site). Older events carry only the count; `previousWeeks`
  // (the scope's pin count before the write) lets the changelog tell a removal apart.
  dated_override_set: {
    weeks: number
    previousWeeks?: number
    firstWeek?: string
    lastWeek?: string
  }
  share_set: { cells: number; previousCells?: number }
  adjustment_add: { groupId: string } & AdjustmentAudit &
    ({ adjustmentIds: string[] } | { adjustmentId: string; appliesTo: "collection" })
  // `groupId`: a whole-campaign write (`saveCampaign`, `deleteCampaigns`, `setCampaignVariants`).
  adjustment_edit: {
    adjustmentId: string
    appliesTo?: "collection"
    groupId?: string
  } & AdjustmentAudit
  adjustment_delete: {
    adjustmentId: string
    appliesTo?: "collection"
    groupId?: string
    name: string
    type: string
    stage: string
    theme: string | null
  }
  // `stage` is absent when one reorder spans stages.
  adjustment_reorder: { ids: string[]; stage?: string }
  insight_set: {
    insightKeys?: string[]
    variants?: number
    appliesTo?: "collection"
  }
  // `excludedSkuCount` is stored because trimEventMeta truncates arrays past 20.
  size_split_set: { skuCount: number; excludedSkuCount?: number; excludedSizes?: string[] }
  // `before`/`after` are option ids (`null` = unanswered). `variantId` is absent or "" when wider.
  framing_set: {
    question: string
    segment: string
    groupKey: string
    groupValue: string
    variantId?: string
    // Bulk reset of several variants: the event's `variantIds` names them; `variantId` is blank.
    variantCount?: number
    // Sales-slice answers the edit deleted: a hierarchy change, or one answer set for every slice.
    slicesReset?: number
    before: string | null
    after: string | null
  }
  // `null` = follows the collection. Rival keys silenced on the node are not named.
  node_insight_set: {
    groupKey: string
    groupValue: string
    insightKey: string
    before: boolean | null
    after: boolean | null
  }
  // `count` is stored because trimEventMeta truncates arrays past 20.
  combinations_excluded_set: { excluded: boolean; count: number; combinations: string[] }
  insights_reviewed_set: { reviewed: boolean }
  // `count` is stored because variantIds can be large; `reset` = the whole plan back to Pending.
  variant_review_set: { reviewed: boolean; count: number; reset?: true }
  match_approve: MatchScope & { matchIds?: string[]; variantCount?: number }
  match_remove: MatchScope & { matchIds?: string[]; reset?: boolean }
  match_basis_set: MatchScope & { matchIds?: string[]; bases: string[] }
  match_none_set: MatchScope & { baseline?: number; preorderShare?: number }
  match_none_clear: MatchScope
  // `source` null = back to the engine's rule; `previous` is what it replaced.
  baseline_source_set: {
    kind: "noos"
    source: BaselineSource | null
    previous: BaselineSource | null
  }
  npi_level_set: { kind: "noos"; reset?: true; introWeight?: number }
  // Values are the weight (0-1); `reset` = back to the model's. Batch only: `variantCount`.
  npi_intro_weight_set: { kind: "noos"; reset?: true; variantCount?: number }
  publish: PublishMeta
}>

// A revert reuses the undone entry's action (keeping its layer and filters); the carried keys let
// it classify (`stage`) and render the reverted change's label.
export type DemandRevertMeta = {
  revertOf: number
  revertedAction: DemandEditAction
  stage?: string
  name?: string
  type?: string
  theme?: string | null
  weeks?: number
  appliesTo?: string
  insightKeys?: string[]
  variantCount?: number
}

const CARRIED_META_KEYS = [
  "stage",
  "name",
  "type",
  "theme",
  "weeks",
  "appliesTo",
  "insightKeys",
  "variantCount",
] as const satisfies readonly (keyof DemandRevertMeta)[]

// The payload a revert of `event` writes. Cast because the source is a stored row.
export function revertMetaFor(event: {
  id: number
  action: string
  meta: unknown
}): DemandRevertMeta {
  const meta = (event.meta ?? {}) as Record<string, unknown>
  const carried = Object.fromEntries(
    CARRIED_META_KEYS.filter((k) => meta[k] !== undefined).map((k) => [k, meta[k]]),
  )
  return { revertOf: event.id, revertedAction: event.action, ...carried } as DemandRevertMeta
}

export type DemandEditMeta<A extends DemandEditAction> =
  | DemandEditMetaByAction[A]
  | (A extends RevertibleAction ? DemandRevertMeta : never)

export type DemandEditPayload = {
  [A in DemandEditAction]: { action: A; meta: DemandEditMeta<A> | null }
}[DemandEditAction]

// The seam between stored JSON and the declared payloads; a pure cast (the append-only ledger has
// no shape to normalise to). An unknown `action` flows to the readers' `default` branches.
export function parseDemandEditMeta(action: string, meta: unknown): DemandEditPayload {
  return { action, meta: meta ?? null } as DemandEditPayload
}

// Actions whose layer depends on the stage recorded in their meta.
export const STAGED_ACTIONS: DemandEditAction[] = [
  "adjustment_add",
  "adjustment_edit",
  "adjustment_delete",
  "adjustment_reorder",
]

const STAGED = new Set<string>(STAGED_ACTIONS)

// Stage for staged rows that recorded none. The SQL layer filter must COALESCE to this same value.
export const DEFAULT_ADJUSTMENT_STAGE = "overlay"

export function layerForAction(
  action: string,
  stage: string | null | undefined,
): DemandChangeLayer {
  // A stored `action` may be one since dropped from the union.
  const layer = LAYER_BY_ACTION[action as DemandEditAction] ?? "manual"
  if (layer !== "on-top") return layer
  return stage === "setup" || stage === "manual" ? stage : layer
}

// layerFilter inverts layerForAction into two SQL predicates callers must OR:
// `action IN (…) OR (adjustment AND stage = …)`.
const STAGE_BY_LAYER: Record<
  DemandChangeLayer,
  "setup" | "manual" | typeof DEFAULT_ADJUSTMENT_STAGE | null
> = {
  setup: "setup",
  manual: "manual",
  "on-top": DEFAULT_ADJUSTMENT_STAGE,
  size: null,
  plan: null,
}

export function layerFilter(layer: DemandChangeLayer): {
  actions: DemandEditAction[]
  stage: "setup" | "manual" | typeof DEFAULT_ADJUSTMENT_STAGE | null
} {
  const actions = (Object.keys(LAYER_BY_ACTION) as DemandEditAction[]).filter(
    (a) => LAYER_BY_ACTION[a] === layer && !STAGED.has(a),
  )
  return { actions, stage: STAGE_BY_LAYER[layer] }
}
